import { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { admin } from '../../utils/api';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, ShoppingCart, DollarSign, Store, Users,
  Heart, CreditCard, Banknote, ShieldCheck, Zap, AlertCircle,
  CheckCircle2, Clock, TrendingUp, PlayCircle
} from 'lucide-react';

const KpiCard = ({ icon: Icon, label, value, sublabel, accent = 'blue', testId }) => {
  const accents = {
    blue: 'from-blue-500 to-indigo-600 text-blue-50',
    emerald: 'from-emerald-500 to-teal-600 text-emerald-50',
    amber: 'from-amber-500 to-orange-600 text-amber-50',
    purple: 'from-purple-500 to-pink-600 text-purple-50',
    slate: 'from-slate-600 to-slate-800 text-slate-50',
  };
  return (
    <Card className="overflow-hidden border-0 shadow-md" data-testid={testId}>
      <div className={`bg-gradient-to-br ${accents[accent]} p-5`}>
        <div className="flex items-start justify-between mb-3">
          <Icon className="w-6 h-6 opacity-90" />
        </div>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        <div className="text-sm opacity-90 mt-1">{label}</div>
        {sublabel && <div className="text-xs opacity-75 mt-2">{sublabel}</div>}
      </div>
    </Card>
  );
};

const StatusPill = ({ ok, label, testId }) => (
  <div className="flex items-center gap-2 text-sm" data-testid={testId}>
    {ok ? (
      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
    ) : (
      <AlertCircle className="w-4 h-4 text-amber-600" />
    )}
    <span className={ok ? 'text-emerald-700' : 'text-amber-700'}>{label}</span>
  </div>
);

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => Number(n || 0).toLocaleString('en-US');

export default function AdminPilotHealth({ user, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [runningFriday, setRunningFriday] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const res = await admin.pilotHealth();
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load pilot health');
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // Auto-refresh every 60s while page is open
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const runFriday = async () => {
    if (!window.confirm('Run the Friday manual-bank payout batch now? This will create pending payouts and email the admin a CSV.')) return;
    try {
      setRunningFriday(true);
      const res = await admin.runFridayPayoutBatch();
      const r = res.data?.result || {};
      toast.success(`Friday batch: ${r.count || 0} payouts, ${fmtUSD(r.total)} ${r.emailed ? '(emailed)' : '(no email)'}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Friday batch failed');
    } finally {
      setRunningFriday(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-20 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading pilot health…
        </div>
      </AdminLayout>
    );
  }

  if (!data) return null;

  const { orders, retailers, consumers, charities: charityStats, payouts, system, recent_orders } = data;

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-8 pb-12">
        {/* Hero */}
        <div className="bg-gradient-to-r from-slate-900 to-indigo-900 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5" />
                <span className="text-sm uppercase tracking-widest opacity-80">Pilot Health</span>
              </div>
              <h1 className="text-4xl font-bold mb-2">V1.1 Pilot Pulse</h1>
              <p className="text-indigo-100">
                Last refreshed {new Date(data.generated_at).toLocaleTimeString()} · auto-refreshes every 60s
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={load}
              disabled={refreshing}
              data-testid="pilot-health-refresh"
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* System banner */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatusPill
                ok={system.stripe_mode === 'live'}
                label={`Stripe mode: ${system.stripe_mode}`}
                testId="ph-stripe-mode"
              />
              <StatusPill
                ok={system.stripe_webhook_secret_set}
                label={`Webhook signing: ${system.stripe_webhook_secret_set ? 'enforced' : 'NOT SET'}`}
                testId="ph-webhook-secret"
              />
              <StatusPill
                ok={system.bank_encryption_key_set}
                label={`Bank encryption: ${system.bank_encryption_key_set ? 'active' : 'dev fallback'}`}
                testId="ph-bank-encryption"
              />
            </div>
          </CardContent>
        </Card>

        {/* Today / 7d / All-time */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Volume</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KpiCard
              testId="ph-orders-today"
              icon={ShoppingCart}
              label="Orders Today"
              value={fmtNum(orders.today.count)}
              sublabel={`${fmtUSD(orders.today.gmv)} GMV`}
              accent="blue"
            />
            <KpiCard
              testId="ph-orders-7d"
              icon={TrendingUp}
              label="Orders Last 7 Days"
              value={fmtNum(orders.last_7_days.count)}
              sublabel={`${fmtUSD(orders.last_7_days.gmv)} GMV`}
              accent="emerald"
            />
            <KpiCard
              testId="ph-orders-all"
              icon={DollarSign}
              label="All-Time GMV"
              value={fmtUSD(orders.all_time.gmv)}
              sublabel={`${fmtNum(orders.all_time.count)} total orders · ${fmtUSD(orders.all_time.platform_fee)} platform fee`}
              accent="purple"
            />
          </div>
        </div>

        {/* Retailer breakdown + Consumers + Charity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-0 shadow-md lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Store className="w-5 h-5 text-blue-600" />
                Retailers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-100" data-testid="ph-retailer-total">
                  <div className="text-2xl font-bold text-blue-900">{fmtNum(retailers.total)}</div>
                  <div className="text-xs text-blue-700 mt-1">Total</div>
                </div>
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-100" data-testid="ph-retailer-live">
                  <div className="text-2xl font-bold text-emerald-900">{fmtNum(retailers.live)}</div>
                  <div className="text-xs text-emerald-700 mt-1">Live</div>
                </div>
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-100" data-testid="ph-retailer-pending">
                  <div className="text-2xl font-bold text-amber-900">{fmtNum(retailers.pending_review)}</div>
                  <div className="text-xs text-amber-700 mt-1">Pending Review</div>
                </div>
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-100" data-testid="ph-retailer-stripe">
                  <div className="flex items-center gap-1 mb-1">
                    <Zap className="w-3 h-3 text-purple-700" />
                    <div className="text-2xl font-bold text-purple-900">{fmtNum(retailers.stripe_connected)}</div>
                  </div>
                  <div className="text-xs text-purple-700">Stripe Connected</div>
                </div>
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid="ph-retailer-manual">
                  <div className="flex items-center gap-1 mb-1">
                    <Banknote className="w-3 h-3 text-slate-700" />
                    <div className="text-2xl font-bold text-slate-900">{fmtNum(retailers.manual_bank)}</div>
                  </div>
                  <div className="text-xs text-slate-700">Manual Bank</div>
                </div>
                <div className="p-4 rounded-lg bg-rose-50 border border-rose-100" data-testid="ph-retailer-unconfigured">
                  <div className="flex items-center gap-1 mb-1">
                    <AlertCircle className="w-3 h-3 text-rose-700" />
                    <div className="text-2xl font-bold text-rose-900">{fmtNum(retailers.unconfigured_payment)}</div>
                  </div>
                  <div className="text-xs text-rose-700">No Payment Setup</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Community
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100" data-testid="ph-consumers">
                  <div className="text-3xl font-bold text-indigo-900">{fmtNum(consumers.total)}</div>
                  <div className="text-xs text-indigo-700 mt-1">Consumers signed up</div>
                </div>
                <div className="p-3 rounded-lg bg-pink-50 border border-pink-100" data-testid="ph-charities">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-pink-600" />
                    <div className="text-3xl font-bold text-pink-900">
                      {fmtNum(charityStats.active)}<span className="text-base font-normal text-pink-700"> / {fmtNum(charityStats.total)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-pink-700 mt-1">Active charities</div>
                </div>
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-100" data-testid="ph-charity-totals">
                  <div className="text-2xl font-bold text-rose-900">{fmtUSD(orders.all_time.charity_total)}</div>
                  <div className="text-xs text-rose-700 mt-1">Lifetime charity giving</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payouts */}
        <Card className="border-0 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Payouts
            </CardTitle>
            <Button
              onClick={runFriday}
              disabled={runningFriday}
              data-testid="ph-run-friday-batch"
              variant="outline"
              size="sm"
            >
              <PlayCircle className={`w-4 h-4 mr-2 ${runningFriday ? 'animate-spin' : ''}`} />
              Run Friday Batch Now
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white" data-testid="ph-payouts-stripe">
                <Zap className="w-5 h-5 mb-2 opacity-90" />
                <div className="text-2xl font-bold">{fmtUSD(payouts.pending_stripe.amount)}</div>
                <div className="text-xs opacity-90 mt-1">{fmtNum(payouts.pending_stripe.count)} pending Stripe</div>
              </div>
              <div className="p-5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white" data-testid="ph-payouts-manual">
                <Banknote className="w-5 h-5 mb-2 opacity-90" />
                <div className="text-2xl font-bold">{fmtUSD(payouts.pending_manual_bank.amount)}</div>
                <div className="text-xs opacity-90 mt-1">{fmtNum(payouts.pending_manual_bank.count)} pending manual ACH</div>
              </div>
              <div className="p-5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white" data-testid="ph-payouts-completed">
                <CheckCircle2 className="w-5 h-5 mb-2 opacity-90" />
                <div className="text-2xl font-bold">{fmtUSD(payouts.completed_last_30d.amount)}</div>
                <div className="text-xs opacity-90 mt-1">{fmtNum(payouts.completed_last_30d.count)} paid (30d)</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-600" />
              Recent Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recent_orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No orders yet — pilot just launched.</div>
            ) : (
              <div className="space-y-2">
                {recent_orders.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`ph-order-${o.id?.slice(0, 8)}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <div>
                        <div className="font-mono text-sm text-gray-900">{o.id?.slice(0, 8)}</div>
                        <div className="text-xs text-gray-500">{new Date(o.created_at).toLocaleString()}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{o.status}</Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-800">{o.payment_status}</Badge>
                      <div className="font-semibold text-gray-900">{fmtUSD(o.total)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
